/**
 * NoteCantiereTab — sezione note generali (multi-record) per un cantiere.
 * SEPARATA dalle "note importanti" delle singole attività Gantt.
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, AlertTriangle, X, Save, Clock, User } from "lucide-react";
import api from "@/api";

const fmtDT = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function NoteCantiereTab({ cantiereId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState(null); // null | 'new' | nota_id
  const [form, setForm] = React.useState({ testo: "", importante: false });

  const { data: note = [], isLoading } = useQuery({
    queryKey: ["cantiere-note", cantiereId],
    queryFn: () => api.get(`/cantieri/${cantiereId}/note`).then((r) => r.data),
  });

  const save = useMutation({
    mutationFn: (p) =>
      p.nota_id
        ? api.patch(`/cantieri/${cantiereId}/note/${p.nota_id}`, { testo: p.testo, importante: p.importante }).then((r) => r.data)
        : api.post(`/cantieri/${cantiereId}/note`, { testo: p.testo, importante: p.importante }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cantiere-note", cantiereId] });
      setEditing(null); setForm({ testo: "", importante: false });
      toast.success("Nota salvata");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const del = useMutation({
    mutationFn: (nid) => api.delete(`/cantieri/${cantiereId}/note/${nid}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cantiere-note", cantiereId] });
      toast.success("Nota eliminata");
    },
  });

  const startEdit = (n) => { setEditing(n.nota_id); setForm({ testo: n.testo, importante: !!n.importante }); };
  const startNew  = () => { setEditing("new"); setForm({ testo: "", importante: false }); };
  const cancel    = () => { setEditing(null); setForm({ testo: "", importante: false }); };
  const submit    = () => {
    if (!form.testo.trim()) return toast.error("Testo mancante");
    save.mutate({ ...form, nota_id: editing === "new" ? null : editing });
  };

  return (
    <div className="space-y-4" data-testid="tab-note-cantiere">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Note generali del cantiere</div>
          <p className="text-xs text-slate-500">Separate dalle "Note importanti" delle attività Gantt. Multi-record con storico e autore.</p>
        </div>
        {!editing && (
          <button onClick={startNew} data-testid="btn-nuova-nota"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-sm">
            <Plus className="w-4 h-4" /> Nuova nota
          </button>
        )}
      </div>

      {editing && (
        <div className="bg-white border-2 border-blue-300 rounded-md p-4 space-y-3" data-testid="nota-editor">
          <div className="overline text-blue-700">{editing === "new" ? "Nuova nota" : "Modifica nota"}</div>
          <textarea rows={4} value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })}
            placeholder="Scrivi la nota..." autoFocus data-testid="nota-testo"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.importante} onChange={(e) => setForm({ ...form, importante: e.target.checked })}
              data-testid="nota-importante" />
            <span className="inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-rose-600" /> Segna come <b>IMPORTANTE</b> (evidenziata in rosso)</span>
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={cancel} className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 text-sm font-semibold rounded-sm">Annulla</button>
            <button onClick={submit} disabled={save.isPending} data-testid="btn-salva-nota"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-sm">
              <Save className="w-4 h-4" /> {save.isPending ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-slate-400 text-sm">Caricamento…</div>
      ) : note.length === 0 && !editing ? (
        <div className="bg-slate-50 border border-slate-200 p-8 text-center text-sm text-slate-500 rounded-sm">
          Nessuna nota. Aggiungi la prima con "Nuova nota".
        </div>
      ) : (
        <ul className="space-y-3">
          {note.map((n) => (
            <li key={n.nota_id} data-testid={`nota-${n.nota_id}`}
              className={`p-4 border rounded-md ${n.importante ? "bg-rose-50 border-rose-300" : "bg-white border-slate-200"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  {n.importante && (
                    <div className="text-[10px] uppercase font-black text-rose-700 inline-flex items-center gap-1 mb-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Importante
                    </div>
                  )}
                  <p className={`whitespace-pre-wrap text-sm ${n.importante ? "text-rose-900 font-semibold" : "text-slate-800"}`}>{n.testo}</p>
                  <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {n.autore_nome || n.autore_email}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDT(n.created_at)}</span>
                    {n.updated_at && n.updated_at !== n.created_at && (
                      <span className="italic">mod. {fmtDT(n.updated_at)}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => startEdit(n)} data-testid={`btn-edit-nota-${n.nota_id}`}
                    className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => window.confirm("Eliminare questa nota?") && del.mutate(n.nota_id)}
                    data-testid={`btn-del-nota-${n.nota_id}`}
                    className="p-1.5 text-rose-500 hover:bg-rose-100 rounded-sm"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
